/* =========================================================
   ASHTON ATHLETICS — theme.js
   No dependencies. Every module is idempotent and re-runs on
   Shopify theme-editor section load so the editor stays live.
   ========================================================= */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveal ---------- */
  function initReveal(root) {
    var items = (root || document).querySelectorAll('.aa-reveal:not([data-revealed])');
    if (!items.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.classList.add('is-visible');
        el.setAttribute('data-revealed', '');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    items.forEach(function (el, i) {
      el.setAttribute('data-revealed', '');
      el.style.transitionDelay = Math.min(i, 4) * 70 + 'ms';
      observer.observe(el);
    });
  }

  /* ---------- Sticky header ---------- */
  function initHeader() {
    var header = document.querySelector('[data-header]');
    if (!header || header.dataset.bound) return;
    header.dataset.bound = '1';

    var update = function () {
      header.classList.toggle('is-stuck', window.scrollY > 24);
    };
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ---------- Password page: reveal the store password form ---------- */
  function initPasswordToggle(root) {
    /* A wrong password reloads the page: reopen the form so the error is seen. */
    var errored = (root || document).querySelector('[data-password-error]');
    if (errored) {
      var panel = errored.closest('.aa-pw__login');
      if (panel) {
        panel.classList.add('is-open');
        var trigger = document.querySelector('[data-password-toggle]');
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
      }
    }

    (root || document).querySelectorAll('[data-password-toggle]:not([data-bound])').forEach(function (btn) {
      btn.dataset.bound = '1';
      btn.addEventListener('click', function () {
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (!panel) return;
        var open = panel.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) {
          var input = panel.querySelector('input[type="password"]');
          if (input) input.focus();
        }
      });
    });
  }

  /* ---------- Before / after compare ---------- */
  function initCompare(root) {
    (root || document).querySelectorAll('[data-compare]:not([data-bound])').forEach(function (stage) {
      stage.dataset.bound = '1';

      var range = stage.querySelector('[data-compare-range]');
      var after = stage.querySelector('[data-compare-after]');
      var divider = stage.querySelector('[data-compare-divider]');
      var knob = stage.querySelector('[data-compare-knob]');
      if (!range || !after) return;

      var paint = function () {
        var value = range.value;
        after.style.clipPath = 'inset(0 0 0 ' + value + '%)';
        if (divider) divider.style.left = value + '%';
        if (knob) knob.style.left = value + '%';
      };

      range.addEventListener('input', paint);
      paint();
    });
  }

  /* ---------- Product gallery thumbnails ---------- */
  function initGallery(root) {
    (root || document).querySelectorAll('[data-gallery]:not([data-bound])').forEach(function (gallery) {
      gallery.dataset.bound = '1';

      var main = gallery.querySelector('[data-gallery-main]');
      var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-thumb]'));
      if (!main || !thumbs.length) return;

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var src = thumb.getAttribute('data-src');
          var srcset = thumb.getAttribute('data-srcset');
          var alt = thumb.getAttribute('data-alt') || '';
          if (!src) return;

          main.setAttribute('src', src);
          if (srcset) main.setAttribute('srcset', srcset);
          main.setAttribute('alt', alt);

          thumbs.forEach(function (other) { other.setAttribute('aria-current', 'false'); });
          thumb.setAttribute('aria-current', 'true');
        });
      });

      /* Swipe the photo between images on touch devices, and click the
         prev/next arrow buttons on desktop (theme-premium.css PASS 21/22
         hides the visible thumbnail strip behind small dot indicators at
         every breakpoint, and PASS 24 adds the arrow buttons themselves).
         Both reuse the same thumbnail click handler above, so image swap
         + aria-current + dot state all stay in perfect sync instead of
         duplicating the logic three ways. */
      if (thumbs.length > 1) {
        var goTo = function (delta) {
          var current = thumbs.findIndex(function (t) { return t.getAttribute('aria-current') === 'true'; });
          if (current === -1) current = 0;
          var next = (current + delta + thumbs.length) % thumbs.length;
          thumbs[next].click();
        };

        var touchStartX = null;
        var touchStartY = null;

        main.addEventListener('touchstart', function (event) {
          var touch = event.changedTouches[0];
          touchStartX = touch.clientX;
          touchStartY = touch.clientY;
        }, { passive: true });

        main.addEventListener('touchend', function (event) {
          if (touchStartX === null) return;
          var touch = event.changedTouches[0];
          var deltaX = touch.clientX - touchStartX;
          var deltaY = touch.clientY - touchStartY;
          touchStartX = null;
          touchStartY = null;

          /* Ignore short taps and mostly-vertical swipes (page scroll). */
          if (Math.abs(deltaX) < 32 || Math.abs(deltaX) < Math.abs(deltaY)) return;
          goTo(deltaX < 0 ? 1 : -1);
        }, { passive: true });

        var prevBtn = gallery.querySelector('[data-gallery-prev]');
        var nextBtn = gallery.querySelector('[data-gallery-next]');
        if (prevBtn) prevBtn.addEventListener('click', function () { goTo(-1); });
        if (nextBtn) nextBtn.addEventListener('click', function () { goTo(1); });
      }
    });
  }

  /* ---------- Variant selection ---------- */
  function initVariants(root) {
    (root || document).querySelectorAll('[data-variant-select]:not([data-bound])').forEach(function (select) {
      select.dataset.bound = '1';

      var form = select.closest('form');
      if (!form) return;
      var idInput = form.querySelector('[data-variant-id]');
      var priceEl = document.getElementById(select.getAttribute('data-price-target') || '');
      var submit = form.querySelector('[data-add-to-cart]');

      select.addEventListener('change', function () {
        var option = select.options[select.selectedIndex];
        if (idInput) idInput.value = option.value;
        if (priceEl && option.dataset.price) priceEl.textContent = option.dataset.price;
        if (submit) {
          var available = option.dataset.available === 'true';
          submit.disabled = !available;
          submit.textContent = available
            ? submit.getAttribute('data-label-available')
            : submit.getAttribute('data-label-soldout');
        }
      });
    });
  }


  /* ---------- Set efficiency stepper ---------- */
  function initSets(root) {
    (root || document).querySelectorAll('[data-sets]:not([data-bound])').forEach(function (widget) {
      widget.dataset.bound = '1';

      var tabs = widget.querySelectorAll('[data-set-tab]');
      var title = widget.querySelector('[data-set-title]');
      var copy = widget.querySelector('[data-set-copy]');
      var tensionBar = widget.querySelector('[data-set-tension-bar]');
      var fatigueBar = widget.querySelector('[data-set-fatigue-bar]');
      var tensionValue = widget.querySelector('[data-set-tension-value]');
      var fatigueValue = widget.querySelector('[data-set-fatigue-value]');
      if (!tabs.length) return;

      var show = function (index) {
        var tab = tabs[index];
        if (!tab) return;

        tabs.forEach(function (other, i) {
          other.setAttribute('aria-selected', i === index ? 'true' : 'false');
        });

        if (title) title.textContent = tab.getAttribute('data-title') || '';
        if (copy) copy.textContent = tab.getAttribute('data-copy') || '';

        var tension = tab.getAttribute('data-tension') || '0';
        var fatigue = tab.getAttribute('data-fatigue') || '0';
        if (tensionBar) tensionBar.style.width = tension + '%';
        if (fatigueBar) fatigueBar.style.width = fatigue + '%';
        if (tensionValue) tensionValue.textContent = tension + '%';
        if (fatigueValue) fatigueValue.textContent = fatigue + '%';
      };

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { show(i); });
        tab.addEventListener('mouseenter', function () { show(i); });
        tab.addEventListener('keydown', function (event) {
          var next = null;
          if (event.key === 'ArrowRight') next = (i + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
          if (next === null) return;
          event.preventDefault();
          tabs[next].focus();
          show(next);
        });
      });

      /* Fill the bars once the widget is on screen so the motion is seen. */
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries, obs) {
          if (!entries[0].isIntersecting) return;
          show(0);
          obs.disconnect();
        }, { threshold: 0.25 });
        io.observe(widget);
      } else {
        show(0);
      }
    });
  }

  /* ---------- Sarcomere contraction ---------- */
  function initSarcomere(root) {
    (root || document).querySelectorAll('[data-sarcomere-scrub]:not([data-bound])').forEach(function (scrub) {
      scrub.dataset.bound = '1';

      var card = scrub.closest('.aa-anatomy__card') || document;
      var zLeft = card.querySelector('[data-sarcomere-z-left]');
      var zRight = card.querySelector('[data-sarcomere-z-right]');
      var actinLeft = card.querySelector('[data-sarcomere-actin-left]');
      var actinRight = card.querySelector('[data-sarcomere-actin-right]');

      var paint = function () {
        var shift = (scrub.value / 100) * 26;
        if (zLeft) zLeft.setAttribute('transform', 'translate(' + shift + ',0)');
        if (actinLeft) actinLeft.setAttribute('transform', 'translate(' + shift + ',0)');
        if (zRight) zRight.setAttribute('transform', 'translate(' + -shift + ',0)');
        if (actinRight) actinRight.setAttribute('transform', 'translate(' + -shift + ',0)');
      };

      scrub.addEventListener('input', paint);
      paint();
    });
  }

  /* ---------- Step sequence ---------- */
  function initSteps(root) {
    (root || document).querySelectorAll('[data-steps]:not([data-bound])').forEach(function (group) {
      group.dataset.bound = '1';

      var steps = group.querySelectorAll('[data-step]');
      if (!steps.length) return;

      var select = function (index) {
        steps.forEach(function (step, i) {
          step.setAttribute('aria-pressed', i === index ? 'true' : 'false');
        });
      };

      steps.forEach(function (step, i) {
        step.addEventListener('click', function () { select(i); });
        step.addEventListener('mouseenter', function () { select(i); });
        step.addEventListener('focus', function () { select(i); });
      });
    });
  }

  /* ---------- Boot ---------- */
  function boot(root) {
    initHeader();
    initReveal(root);
    initPasswordToggle(root);
    initCompare(root);
    initGallery(root);
    initVariants(root);
    initSets(root);
    initSarcomere(root);
    initSteps(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  /* Keep everything working while the merchant edits in the theme editor */
  document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
  document.addEventListener('shopify:section:unload', function () { /* observers self-clean */ });
})();
